import { api } from "jiffy-api";
import { withCsrfRetry } from "common/utils/csrf-token-manager";
import trackEtaEstimates from "./track-eta-estimates";
import businessLineToUrlGroup from "./business-line-group";

const PRODUCT_PAGE_CONTAINER_ID = "product-page-container";
const EMPTY_VARIANTS_HASH = JSON.stringify({});
const INITIAL_COMPOSITION_QUANTITY = 1;

const getProductPageDataset = () => document.getElementById(PRODUCT_PAGE_CONTAINER_ID)?.dataset;

const readFeatureFlipFromDataset = () => getProductPageDataset()?.fastDeliveryEstimate === "true";

const readFeatureFlipFromWindow = () => window?.jiffy?.flip?.fastDeliveryEstimate === true;

export const isFastDeliveryEstimateEnabled = () =>
  readFeatureFlipFromDataset() || readFeatureFlipFromWindow();

const extractVariantsHash = payload => {
  const source = payload?.delivery_estimate ?? payload ?? {};
  return source.variants_hash || EMPTY_VARIANTS_HASH;
};

const readCompositionIdFromDataset = () => getProductPageDataset()?.compositionId;

const readBusinessLineFromDataset = () => getProductPageDataset()?.businessLine;

const buildCompositionHash = compositionId => {
  if (!compositionId) return null;
  return JSON.stringify({ [compositionId]: INITIAL_COMPOSITION_QUANTITY });
};

const buildShipment = date => ({
  date,
  date_with_day_name: date,
  full_date: null,
  show_logo: false,
  items: 1
});

const buildLegacyDates = estimate => {
  const date = estimate?.date;
  if (!date) return null;

  const timeleft = estimate?.timeleft || null;
  const cutoffLabel = estimate?.cutoff_label || null;
  const shipment = buildShipment(date);

  return {
    show_logo: false,
    value_full: date,
    size: 1,
    timeleft,
    cutoff_label: cutoffLabel,
    delivery_type: null,
    delivery_time: null,
    delivery_time_end: null,
    shipments: [shipment],
    only_cart_shipments: [buildShipment(date)],
    shipping_cost_info: null
  };
};

const extractDtfArtworkIds = payload => {
  const sources = [payload?.dtf_print, payload?.delivery_estimate?.dtf_print];
  const ids = new Set();
  sources.forEach(source => {
    if (!Array.isArray(source)) return;
    source.forEach(entry => {
      // Prefer dtf_artwork_id: in multi-size mode artwork_id is mangled
      // ("<id>-<index>") for per-size keying and is not parseable as a number.
      const raw = entry?.dtf_artwork_id ?? entry?.artwork_id ?? entry;
      const num = typeof raw === "number" ? raw : Number(raw);
      if (Number.isFinite(num) && num > 0) ids.add(num);
    });
  });
  return Array.from(ids);
};

const buildEtaQuery = (variantsHash, compositionHash, dtfArtworkIds) => {
  const isInitialRequest = variantsHash === EMPTY_VARIANTS_HASH;
  const base =
    isInitialRequest && compositionHash
      ? { composition_hash: compositionHash }
      : { variants_hash: variantsHash };
  if (Array.isArray(dtfArtworkIds) && dtfArtworkIds.length > 0) {
    base.dtf_artwork_ids = dtfArtworkIds;
  }
  return base;
};

const parseHashKeys = hash => {
  if (!hash || hash === EMPTY_VARIANTS_HASH) return [];
  try {
    const parsed = JSON.parse(hash);
    return parsed && typeof parsed === "object" ? Object.keys(parsed) : [];
  } catch {
    return [];
  }
};

const pickEstimateForRequest = (estimates, compositionId, requestedKeys) => {
  if (compositionId && estimates[compositionId]) return estimates[compositionId];
  // MN-5755: variants_hash requests come back keyed by variant id, not
  // composition id. Match by the keys we actually sent so variant-aware
  // fetches don't fail the compositionId lookup and trigger the MN-5472
  // failure path.
  const matchedKey = requestedKeys.find(key => estimates[key]);
  if (matchedKey) return estimates[matchedKey];
  // Legacy path: when neither a composition id nor specific request keys were
  // supplied, accept whatever estimate came back. MN-5472's strict failure
  // path only applies when we explicitly asked for a key the response lacks.
  if (!compositionId && requestedKeys.length === 0) {
    return Object.values(estimates).find(Boolean) || null;
  }
  return null;
};

const normalizeEtaEstimatesResponse = (response, compositionId, requestedKeys = []) => {
  const estimates = response?.estimates;
  if (!estimates || typeof estimates !== "object") return null;

  const estimate = pickEstimateForRequest(estimates, compositionId, requestedKeys);
  const legacyDates = buildLegacyDates(estimate);
  if (!legacyDates) return null;

  return {
    success: response?.success !== false,
    composition_dates: legacyDates,
    cart_dates: legacyDates,
    mixpanel: response?.mixpanel
  };
};

const normalizeFastEstimateResponse = (response, compositionId, requestedKeys = []) => {
  if (!response) return {};
  if (response.composition_dates || response.cart_dates) return response;
  const retrofitted = normalizeEtaEstimatesResponse(response, compositionId, requestedKeys);
  if (retrofitted) return retrofitted;
  if (response.delivery_estimate) return response.delivery_estimate;
  // MN-5472 / MN-5755: when we asked for a specific composition or variant
  // and no matching estimate came back, signal failure so consumers hit their
  // error path instead of leaking a foreign estimate via the raw ETA payload.
  if (compositionId || requestedKeys.length > 0) return { success: false };
  return response;
};

export const fetchPdpDeliveryEstimates = async ({ productId }, payload = {}) => {
  if (!isFastDeliveryEstimateEnabled()) {
    return api.product.fetchDeliveryEstimatesForCurrentCart({ productId }, payload);
  }

  const compositionId = readCompositionIdFromDataset();
  const urlGroup = businessLineToUrlGroup(readBusinessLineFromDataset());

  if (urlGroup === null) {
    return api.product.fetchDeliveryEstimatesForCurrentCart({ productId }, payload);
  }

  const variantsHash = extractVariantsHash(payload);
  const compositionHash = buildCompositionHash(compositionId);
  const dtfArtworkIds = extractDtfArtworkIds(payload);
  const isInitialRequest = variantsHash === EMPTY_VARIANTS_HASH;
  const sentHash = isInitialRequest ? compositionHash : variantsHash;
  const requestedKeys = parseHashKeys(sentHash);
  const query = {
    ...buildEtaQuery(variantsHash, compositionHash, dtfArtworkIds),
    source: "pdp"
  };

  try {
    const response = await withCsrfRetry(
      () => api.product.fetchETA({ business_line: urlGroup }, query),
      "delivery_estimates"
    );
    trackEtaEstimates(response);
    return normalizeFastEstimateResponse(response, compositionId, requestedKeys);
  } catch (error) {
    return api.product.fetchDeliveryEstimatesForCurrentCart({ productId }, payload);
  }
};
